import React from 'react';
import { useLocation } from 'react-router';
import * as Sentry from '@sentry/react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BoltLoader } from '@/components/ui/bolt-loader';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import {
  forceReload,
  isReloadInFlight,
  isStaleChunkError,
  reloadForStaleChunk,
} from '@/utils/staleChunkReload';

interface ChunkErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this clears a displayed error; the default export feeds it the route. */
  resetKey?: string;
}

interface ChunkErrorBoundaryState {
  error: Error | null;
  recovering: boolean;
}

const RecoveryScreen: React.FC<{ isStale: boolean }> = ({ isStale }) => {
  const { translator } = React.useContext(LanguageProviderContext);
  const prefix = isStale ? 'AppRecovery.update' : 'AppRecovery.error';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-4">
        <h1 className="text-lg font-semibold">{translator(`${prefix}Title`)}</h1>
        <p className="text-muted-foreground">{translator(`${prefix}Description`)}</p>
        <Button onClick={forceReload} className="w-full sm:w-auto">
          <RefreshCw className="h-4 w-4" />
          {translator('AppRecovery.reload')}
        </Button>
      </div>
    </div>
  );
};

const ReloadingScreen: React.FC = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <BoltLoader size="lg" />
  </div>
);

/**
 * Backstop for `vite:preloadError` (see `@/utils/staleChunkReload`): a chunk deleted
 * by a deploy can also surface as a render error thrown by `React.lazy`, which would
 * otherwise unmount the app and reach Sentry as an unhandled crash.
 */
class ChunkErrorBoundaryInner extends React.Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { error: null, recovering: false };

  static getDerivedStateFromError(error: Error): Partial<ChunkErrorBoundaryState> {
    return { error };
  }

  componentDidUpdate(prevProps: ChunkErrorBoundaryProps): void {
    if (!this.state.error) return;
    if (prevProps.resetKey === this.props.resetKey) return;
    // A pending reload is about to replace the document; leave it alone.
    if (isReloadInFlight()) return;
    this.setState({ error: null, recovering: false });
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    if (isStaleChunkError(error)) {
      this.setState({ recovering: true });
      void reloadForStaleChunk().then((reloading) => {
        if (!reloading) this.setState({ recovering: false });
      });
      return;
    }

    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render(): React.ReactNode {
    const { error, recovering } = this.state;
    if (!error) return this.props.children;

    // A reload swallowed by `preventDefault` leaves `React.lazy` with an empty
    // module, so any error raised while navigating away is part of the recovery.
    if (recovering || isReloadInFlight()) return <ReloadingScreen />;

    return <RecoveryScreen isStale={isStaleChunkError(error)} />;
  }
}

/**
 * Feeds the current route to the boundary so a render error is cleared on
 * navigation: the recovery screen replaces the app chrome, so browser
 * back/forward is the only escape that does not need a full reload.
 */
const ChunkErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();

  return (
    <ChunkErrorBoundaryInner resetKey={pathname}>{children}</ChunkErrorBoundaryInner>
  );
};

export default ChunkErrorBoundary;
